import {DATE_FORMAT} from 'consts'
import {noop, omit} from 'lodash'
import moment from 'moment'
import React, {useCallback, useContext, useMemo, useReducer} from 'react'
import {Basic} from 'types'
import {GetterMode} from 'types/basic'
import {getter} from 'utils/getter'
import {hasGuests} from 'utils/guestCounterHelpers'
import {AppearanceProvider} from '../AppearanceProvider'
import {DEFAULT_CART_ITEM_INPUT} from './consts'
import {reducer} from './reducer'
import {InputContextValue} from './types'

type Props = {
  children: React.ReactNode
  initialState: Basic.CartItem
}

const InputContext = React.createContext<InputContextValue>({
  input: DEFAULT_CART_ITEM_INPUT,
  mutators: {
    setOptionId: noop,
    setDate: noop,
    setAvailabilityId: noop,
    setUnitCounters: noop,
  },
})

export const InputProvider = ({children, initialState}: Props) => {
  const stepOrder = AppearanceProvider.useManager().stepOrder

  const [input, dispatch] = useReducer(reducer, {
    ...initialState,
    date: initialState.availabilityId ? moment(initialState.availabilityId).format(DATE_FORMAT) : '',
  })

  const setOptionId = useCallback(
    (optionId: string) => dispatch({type: 'setOptionId', optionId, stepOrder}),
    [stepOrder]
  )

  const setDate = useCallback((date: string) => dispatch({type: 'setDate', date, stepOrder}), [stepOrder])

  const setAvailabilityId = useCallback(
    (availabilityId: string) => dispatch({type: 'setAvailabilityId', availabilityId, stepOrder}),
    [stepOrder]
  )

  const setUnitCounters = useCallback(
    (unitCounters: Basic.UnitCounterMap) => dispatch({type: 'setUnitCounters', unitCounters, stepOrder}),
    [stepOrder]
  )

  const contextValue = useMemo(() => {
    return {
      input,
      mutators: {
        setOptionId,
        setDate,
        setAvailabilityId,
        setUnitCounters,
      },
    }
  }, [input, setOptionId, setDate, setAvailabilityId, setUnitCounters])

  return <InputContext.Provider value={contextValue}>{children}</InputContext.Provider>
}

InputProvider.useContext = () => {
  return useContext(InputContext)
}

InputProvider.useInput = () => {
  const context = InputProvider.useContext()
  return context.input
}

InputProvider.useCartItem = (): Basic.CartItem => {
  const context = InputProvider.useContext()
  return omit(context.input, ['date'])
}

InputProvider.useId = () => {
  const {id} = InputProvider.useInput()
  return id
}

InputProvider.useProductId = () => {
  const {productId} = InputProvider.useInput()
  return productId
}

InputProvider.useOptionId = (mode?: GetterMode) => {
  const {optionId} = InputProvider.useInput()
  return getter(optionId, mode, !!optionId, 'Option id is not set')
}

InputProvider.useDate = (mode?: GetterMode) => {
  const {date} = InputProvider.useInput()
  return getter(date, mode, !date, 'Date is not set')
}

InputProvider.useAvailabilityId = (mode?: GetterMode) => {
  const {availabilityId} = InputProvider.useInput()
  return getter(availabilityId, mode, !!availabilityId, 'Availability id is not set')
}

InputProvider.useUnitCounters = (mode?: GetterMode) => {
  const {unitCounters} = InputProvider.useInput()
  return getter(unitCounters, mode, hasGuests(unitCounters), 'Unit counters is not set')
}

InputProvider.useMutators = () => {
  const {mutators} = InputProvider.useContext()
  return mutators
}
